import { z } from "zod";
import { PartnerFlowSchema } from "@remi/enums/partner-flow";
import { ProjectChecklistSubmissionStatusSchema } from "@remi/enums/project-checklist-submission-status";

/**
 * ====================
 * Checklist Schema
 * ====================
 */

// Question Types
export const ChecklistQuestionTypeValues = [
  "boolean",
  "text",
  "images",
  "dropdown",
  "multiselect",
  "number",
  "date",
] as const;
export const ChecklistQuestionTypeSchema = z.enum(ChecklistQuestionTypeValues);

const ChecklistConditionOperatorValues = ["equals", "not_equals", "in"] as const;
const ChecklistConditionOperatorSchema = z.enum(ChecklistConditionOperatorValues);

// IDs ending in `__r<index>` are reserved for runtime clones produced by
// `reconcileSubmissionSchema`. The reserved-suffix guard CANNOT live in this Zod
// schema — the same schema is used for parsing both templates (where `__r<n>`
// IDs must be rejected) and per-submission snapshots (where `__r<n>` IDs are
// expected on materialized clones). The guard is enforced at the service layer
// via `validateReservedSuffixes` from `./materialize`, called from `createSchema`.
const SchemaIdSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_:.-]+$/, "ID may only contain letters, numbers, underscores, hyphens, colons, and periods");

export const ChecklistConditionSchema = z.object({
  questionId: SchemaIdSchema,
  op: ChecklistConditionOperatorSchema.default("equals"),
  equals: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
  values: z
    .array(z.union([z.string(), z.number(), z.boolean()]))
    .max(200)
    .optional(),
});

const ChecklistRepeatDisplaySchema = z.object({
  // Names each materialized iteration in the UI, e.g. "Slope" → "Slope 1", "Slope 2".
  label: z.string().max(60).optional(),
  // Member question whose answer titles each iteration once filled (e.g. slope_label →
  // "Front / North"), falling back to "<label> <n>" before it's answered.
  labelQuestionId: z.string().optional(),
  // Each iteration's dropdown answer must be distinct within the group, so four elevations
  // cannot all be labelled "Front". Enforced in the picker, per group.
  uniqueOptions: z.boolean().optional(),
});

const ChecklistCountedRepeatSchema = ChecklistRepeatDisplaySchema.extend({
  questionId: z.string(),
  maxCount: z.int().min(1).max(20).optional(),
});

// Always materializes `count` iterations, so nothing asks the inspector how many.
const ChecklistFixedRepeatSchema = ChecklistRepeatDisplaySchema.extend({
  groupId: z.string(),
  count: z.int().min(1).max(20),
});

// Fixed first: a counted repeat has no groupId, so it fails the fixed shape and falls through.
export const ChecklistQuestionRepeatSchema = z.union([ChecklistFixedRepeatSchema, ChecklistCountedRepeatSchema]);

// Question
const ChecklistBaseQuestionSchema = z.object({
  id: SchemaIdSchema,
  prompt: z.string().max(500).optional(),
  description: z.string().max(500).optional(),
  required: z.boolean().optional(),
  condition: ChecklistConditionSchema.optional(),
  repeat: ChecklistQuestionRepeatSchema.optional(),
  // Stamped onto clones by materialization so the renderer can group them into per-iteration sub-sections.
  // repeatGroupId is the source group's stable id (repeat.questionId); the renderer keys grouping by it
  // so two unlabeled repeat groups in one section stay distinct (repeatLabel is header text only).
  repeatIndex: z.int().min(0).optional(),
  repeatLabel: z.string().max(60).optional(),
  repeatGroupId: SchemaIdSchema.optional(),
  repeatUniqueOptions: z.boolean().optional(),
  // Resolved id of this iteration's label question (repeat.labelQuestionId + __r<n>); its answer
  // titles the sub-section when present.
  repeatLabelQuestionId: SchemaIdSchema.optional(),
});

const ChecklistBooleanQuestionSchema = ChecklistBaseQuestionSchema.extend({
  type: z.literal("boolean"),
});

const ChecklistTextQuestionSchema = ChecklistBaseQuestionSchema.extend({
  type: z.literal("text"),
  placeholder: z.string().max(500).optional(),
});

const ChecklistImagesQuestionSchema = ChecklistBaseQuestionSchema.extend({
  type: z.literal("images"),
  minPhotos: z.int().min(0),
  maxPhotos: z.int().min(1).optional(),
});

export const ChecklistDropdownOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const ChecklistDropdownQuestionSchema = ChecklistBaseQuestionSchema.extend({
  type: z.literal("dropdown"),
  options: z.array(ChecklistDropdownOptionSchema).max(200),
  placeholder: z.string().max(500).optional(),
});

const ChecklistMultiselectQuestionSchema = ChecklistBaseQuestionSchema.extend({
  type: z.literal("multiselect"),
  options: z.array(ChecklistDropdownOptionSchema),
  placeholder: z.string().max(500).optional(),
});

const ChecklistNumberQuestionSchema = ChecklistBaseQuestionSchema.extend({
  type: z.literal("number"),
  placeholder: z.string().max(500).optional(),
});

const ChecklistDateQuestionSchema = ChecklistBaseQuestionSchema.extend({
  type: z.literal("date"),
});

export const ChecklistQuestionSchema = z.discriminatedUnion("type", [
  ChecklistBooleanQuestionSchema,
  ChecklistTextQuestionSchema,
  ChecklistImagesQuestionSchema,
  ChecklistDropdownQuestionSchema,
  ChecklistMultiselectQuestionSchema,
  ChecklistNumberQuestionSchema,
  ChecklistDateQuestionSchema,
]);

export const ChecklistSectionSchema = z.object({
  id: SchemaIdSchema,
  title: z.string().max(500).optional(),
  prompt: z.string().max(500).optional(),
  questions: z.array(ChecklistQuestionSchema),
});

// Translations mirror the canonical-field caps so a many-locale payload can't bypass the input size guard.
const ChecklistQuestionTranslationSchema = z.object({
  prompt: z.string().max(500).optional(),
  description: z.string().max(500).optional(),
  placeholder: z.string().max(500).optional(),
  options: z.record(z.string(), z.string().max(500)).optional(),
});

const ChecklistSectionTranslationSchema = z.object({
  title: z.string().max(500).optional(),
  prompt: z.string().max(500).optional(),
});

const ChecklistLocaleTranslationSchema = z.object({
  questions: z.record(z.string(), ChecklistQuestionTranslationSchema).optional(),
  sections: z.record(z.string(), ChecklistSectionTranslationSchema).optional(),
});

export const ChecklistTranslationsSchema = z.record(z.string(), ChecklistLocaleTranslationSchema);

// Schema
export const ChecklistSchemaSchema = z.object({
  version: z.string().max(64),
  sections: z.array(ChecklistSectionSchema).max(100),
  translations: ChecklistTranslationsSchema.optional(),
});

// Inferred TS types
export type ChecklistQuestionType = z.infer<typeof ChecklistQuestionTypeSchema>;
export type ChecklistCondition = z.infer<typeof ChecklistConditionSchema>;
export type ChecklistQuestionRepeat = z.infer<typeof ChecklistQuestionRepeatSchema>;
export type ChecklistCountedRepeat = z.infer<typeof ChecklistCountedRepeatSchema>;
export type ChecklistQuestion = z.infer<typeof ChecklistQuestionSchema>;
export type ChecklistSection = z.infer<typeof ChecklistSectionSchema>;
export type ChecklistSchema = z.infer<typeof ChecklistSchemaSchema>;
export type ChecklistTranslations = z.infer<typeof ChecklistTranslationsSchema>;

// Discriminated question types
export type ChecklistImagesQuestion = z.infer<typeof ChecklistImagesQuestionSchema>;

/**
 * ====================
 * Checklist Answers
 * ====================
 */

// Answer
const ChecklistBaseAnswerSchema = z.object({
  questionId: z.string(),
  // Client fill-time (ISO-8601 UTC). Non-collection answers use it to resolve
  // concurrent edits by newest-wins; missing = oldest (pre-timestamp clients/rows).
  answeredAt: z.iso.datetime().optional(),
});

const ChecklistBooleanAnswerSchema = ChecklistBaseAnswerSchema.extend({
  type: z.literal("boolean"),
  value: z.boolean(),
});

const ChecklistTextAnswerSchema = ChecklistBaseAnswerSchema.extend({
  type: z.literal("text"),
  value: z.string(),
});

const ChecklistImagesAnswerSchema = ChecklistBaseAnswerSchema.extend({
  type: z.literal("images"),
  photoIds: z.array(z.string()),
});

const ChecklistDropdownAnswerSchema = ChecklistBaseAnswerSchema.extend({
  type: z.literal("dropdown"),
  value: z.string(),
});

export const ChecklistMultiselectAnswerSchema = ChecklistBaseAnswerSchema.extend({
  type: z.literal("multiselect"),
  value: z.array(z.string()),
});

/**
 * Numeric answer is stored as a string of digits (e.g. "24") to match the FIC
 * payload shape and to avoid floating-point coercion in JSON. Capped at 15
 * digits — anything longer would lose precision when converted via `Number()`
 * for visibility-condition comparisons (max safe integer is 2^53 ≈ 9×10^15).
 */
export const NUMERIC_STRING_MAX_DIGITS = 15;
const NumericDigitsString = z.string().regex(/^\d+$/).max(NUMERIC_STRING_MAX_DIGITS);

const ChecklistNumberAnswerSchema = ChecklistBaseAnswerSchema.extend({
  type: z.literal("number"),
  value: NumericDigitsString,
});

/**
 * Date answer is stored as a `YYYY-MM-DD` calendar day with no time or zone: a
 * "date of loss" is the day the field user picked on the calendar, and attaching
 * a timezone would shift it by a day for anyone reading it from another offset.
 */
export const ChecklistDateAnswerSchema = ChecklistBaseAnswerSchema.extend({
  type: z.literal("date"),
  value: z.iso.date(),
});

export const ChecklistAnswerSchema = z.discriminatedUnion("type", [
  ChecklistBooleanAnswerSchema,
  ChecklistTextAnswerSchema,
  ChecklistImagesAnswerSchema,
  ChecklistDropdownAnswerSchema,
  ChecklistMultiselectAnswerSchema,
  ChecklistNumberAnswerSchema,
  ChecklistDateAnswerSchema,
]);

/**
 * Schema for ProjectChecklistQuestionSubmission.type + value fields.
 * Images have no value - photoIds come from the photos relation.
 */
export const AnswerValueSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("boolean"), value: z.boolean() }),
  z.object({ type: z.literal("text"), value: z.string() }),
  z.object({ type: z.literal("images") }),
  z.object({ type: z.literal("dropdown"), value: z.string() }),
  z.object({ type: z.literal("multiselect"), value: z.array(z.string()) }),
  z.object({ type: z.literal("number"), value: NumericDigitsString }),
  z.object({ type: z.literal("date"), value: z.iso.date() }),
]);

// Inferred TS types
export type ChecklistAnswer = z.infer<typeof ChecklistAnswerSchema>;
export type ChecklistImagesAnswer = z.infer<typeof ChecklistImagesAnswerSchema>;
export type ChecklistBooleanAnswer = z.infer<typeof ChecklistBooleanAnswerSchema>;
export type ChecklistTextAnswer = z.infer<typeof ChecklistTextAnswerSchema>;
export type ChecklistDropdownAnswer = z.infer<typeof ChecklistDropdownAnswerSchema>;
export type ChecklistMultiselectAnswer = z.infer<typeof ChecklistMultiselectAnswerSchema>;
export type ChecklistNumberAnswer = z.infer<typeof ChecklistNumberAnswerSchema>;
export type ChecklistDateAnswer = z.infer<typeof ChecklistDateAnswerSchema>;

// Answers resolved by fill-time (newest-wins). INVARIANT: each resolved type's stored
// `value` must stay a primitive/array (never a `{ value }` object) — the read extension
// tells a bare value from an `{ value, answeredAt }` envelope structurally.
const TIMESTAMP_RESOLVED_BY_TYPE = {
  boolean: true,
  text: true,
  images: false,
  dropdown: true,
  multiselect: true,
  number: true,
  date: true,
} as const satisfies Record<ChecklistAnswer["type"], boolean>;

export const TIMESTAMP_RESOLVED_TYPES: ReadonlySet<string> = new Set(
  Object.entries(TIMESTAMP_RESOLVED_BY_TYPE)
    .filter(([, isResolved]) => isResolved)
    .map(([type]) => type),
);

/**
 * ====================
 * Checklist Review (Approval/Rejection)
 * ====================
 */

export const ChecklistReviewStatusSchema = z.enum(["APPROVED", "REJECTED"]);
export type ChecklistReviewStatus = z.infer<typeof ChecklistReviewStatusSchema>;

const QuestionReviewStatusValues = ["PENDING", "APPROVED", "REJECTED"] as const;
const QuestionReviewStatusSchema = z.enum(QuestionReviewStatusValues);
export type QuestionReviewStatus = z.infer<typeof QuestionReviewStatusSchema>;

const ChecklistPhotoReviewSchema = z.object({
  status: ChecklistReviewStatusSchema,
  reason: z.string().optional(),
});

const ChecklistQuestionReviewSchema = z.object({
  status: ChecklistReviewStatusSchema,
  reason: z.string().optional(),
  photos: z.record(z.string(), ChecklistPhotoReviewSchema).optional(),
});

export const ChecklistReviewSchema = z.object({
  schemaVersion: z.string(),
  questions: z.record(z.string(), ChecklistQuestionReviewSchema),
});

// Inferred TS types for Review
export type ChecklistQuestionReview = z.infer<typeof ChecklistQuestionReviewSchema>;
export type ChecklistReview = z.infer<typeof ChecklistReviewSchema>;

/*
 * ====================
 * Review Queue
 * ====================
 */

export const ReviewQueueRowSchema = z.object({
  id: z.string(),
  projectId: z.coerce.number(),
  status: ProjectChecklistSubmissionStatusSchema,
  addressStreet: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: z.string().nullable(),
  homeownerName: z.string().nullable(),
  partnerFlow: PartnerFlowSchema,
  projectCategory: z.string(),
  question_count: z.coerce.number(),
  approved_count: z.coerce.number(),
  rejected_count: z.coerce.number(),
  has_unapproved_mobile_photos: z.coerce.boolean(),
  sort_key: z.string().transform((s) => new Date(`${s}Z`)),
});

export type ReviewAddress = { addressStreet: string; city: string; stateCode: string };

/** Distributive Omit for ChecklistAnswer — removes questionId while preserving the discriminated union */
export type AnswerValueWithPhotos =
  | { type: "boolean"; value: boolean }
  | { type: "text"; value: string }
  | { type: "images"; photoIds: string[] }
  | { type: "dropdown"; value: string }
  | { type: "multiselect"; value: string[] }
  | { type: "number"; value: string }
  | { type: "date"; value: string };

export type ReviewPhoto = {
  id: string;
  fileId: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  approvedAt: Date | null;
  flaggedAt: Date | null;
  isUploaded: boolean;
};

export type ReviewAnswer = {
  questionId: string;
  type: string;
  label: string;
  sectionId: string | undefined;
  sectionTitle: string | undefined;
  value: AnswerValueWithPhotos | null;
  photos: ReviewPhoto[];
  updatedAt: Date;
};

const RejectionHistoryItemSchema = z.object({
  type: z.string().optional(),
  reason: z.string().optional(),
  rejectedAt: z.string().optional(),
  rejectedByPersonId: z.string().optional(),
  value: z.string().optional(),
  updatedAt: z.string().optional(),
});
type RejectionHistoryItem = z.infer<typeof RejectionHistoryItemSchema>;

export const RejectionHistorySchema = z.array(RejectionHistoryItemSchema).catch([]);

export type ReviewQuestionReview = {
  id: string;
  questionId: string;
  status: QuestionReviewStatus;
  rejectionReason: string | null;
  rejectionHistory: RejectionHistoryItem[];
  reviewedAt: Date | null;
};
